import {IndexableType} from "dexie";
import axios from "axios";
import {PantheonINI, PantheonLicense} from "../API";
import {db, PantheonExeSession, PantheonNativeProcessState} from "../data/db";
import {UserQuery} from "../graphql-types";

type OnixDesktopIpcResult = {
    ok?: boolean;
    error?: string;
    stderr?: string;
    stdout?: string;
    [key: string]: any;
};

export type PantheonNativeProcessStateMessage = {
    clientSessionId: string;
    state: PantheonNativeProcessState;
    pid?: number;
    processStartedAt?: string;
    exePath?: string;
    iniPath?: string;
    endedAt?: string;
    exitCode?: number | null;
    error?: string;
};

type OnixDesktopApi = {
    getPlatform?: () => Promise<OnixDesktopIpcResult>;
    fileExists?: (path: string) => Promise<OnixDesktopIpcResult>;
    readFile?: (path: string) => Promise<OnixDesktopIpcResult & {contents?: string}>;
    makeDirectory?: (path: string) => Promise<OnixDesktopIpcResult>;
    writeFile?: (path: string, contents: string) => Promise<OnixDesktopIpcResult>;
    downloadFile?: (request: {
        url: string;
        destinationPath: string;
    }) => Promise<OnixDesktopIpcResult>;
    extract7zip?: (request: {
        archivePath: string;
        destinationPath: string;
    }) => Promise<OnixDesktopIpcResult>;
    renameFile?: (request: {
        fromPath: string;
        toPath: string;
    }) => Promise<OnixDesktopIpcResult>;
    executePantheon?: (request: {
        clientSessionId: string;
        authArgument: string;
        exePath: string;
        iniPath: string;
        pantheonLicenseArgument: string;
        platform: "win32" | "darwin" | string;
    }) => Promise<OnixDesktopIpcResult & PantheonNativeProcessStateMessage>;
    reconcilePantheonProcesses?: (request: {
        sessions: Array<{
            clientSessionId: string;
            pid: number;
            processStartedAt: string;
            exePath: string;
            iniPath: string;
        }>;
    }) => Promise<OnixDesktopIpcResult & {states?: PantheonNativeProcessStateMessage[]}>;
    onPantheonProcessState?: (
        listener: (state: PantheonNativeProcessStateMessage) => void,
    ) => () => void;
    openPantheonMeshCentralSqlTunnel?: (request: {
        accessToken?: string;
        pantheonIniId: number;
    }) => Promise<OnixDesktopIpcResult>;
    closePantheonMeshCentralSqlTunnel?: (request: {
        tunnelId: string;
    }) => Promise<OnixDesktopIpcResult>;
    onDeepLink?: (listener: (url: string) => void) => () => void;
};

type MeshCentralSqlTunnel = {
    localPort: number;
    tunnelId: string;
};

export type PantheonSessionStatus =
    | "pantheonSessionStarted"
    | "downloadingPantheonExe"
    | "extractingPantheonExe"
    | "startingPantheonExe"
    | "pantheonSessionFinished"
    | "pantheonSessionError"
    | "movingExtractedPantheonExe"
    | "writingIni"
    | "executingPantheon"
    | "pantheonSessionTerminatedSuccessfully"
    | "pantheonSessionTerminatedWithError";

export class PantheonSessionHelper {
    static staleRunningSessionMilliseconds = 12 * 60 * 60 * 1000;
    static nativeProcessLaunchGraceMilliseconds = 60 * 1000;
    static nativeProcessReconciliationBatchSize = 100;

    static getOnixDesktopApi(): OnixDesktopApi | undefined {
        return (window as any)?.onixDesktop as OnixDesktopApi | undefined;
    }

    static hasOnixDesktopBridge(): boolean {
        return !!PantheonSessionHelper.getOnixDesktopApi();
    }

    static hasOnixDesktopPantheonApi(): boolean {
        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        return !!(
            onixDesktop?.getPlatform &&
            onixDesktop.fileExists &&
            onixDesktop.makeDirectory &&
            onixDesktop.writeFile &&
            onixDesktop.downloadFile &&
            onixDesktop.extract7zip &&
            onixDesktop.renameFile &&
            onixDesktop.executePantheon
        );
    }

    static hasOnixDesktopPantheonProcessApi(): boolean {
        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        return !!(
            onixDesktop?.executePantheon &&
            onixDesktop.reconcilePantheonProcesses &&
            onixDesktop.onPantheonProcessState
        );
    }

    static hasOnixDesktopLocalFileApi(): boolean {
        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        return !!(onixDesktop?.getPlatform && onixDesktop.fileExists && onixDesktop.readFile);
    }

    static canUseWindowRequire(): boolean {
        return typeof (window as any)?.require === "function";
    }

    static async requireOnixDesktopResult<T extends OnixDesktopIpcResult>(
        result: Promise<T>,
    ): Promise<T> {
        const resolved = await result;
        if (resolved?.ok === false) {
            throw {
                message: resolved.error,
                stderr: resolved.stderr ?? resolved.error ?? "OnixDesktop request failed.",
                stdout: resolved.stdout,
            };
        }

        return resolved;
    }

    static async readLocalTextFile(path: string): Promise<string> {
        if (PantheonSessionHelper.canUseWindowRequire()) {
            return window.require("fs").promises.readFile(path, "utf8");
        }

        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        if (!onixDesktop?.readFile) {
            throw {
                stderr: "OnixDesktop ne podržava čitanje lokalnog INIja.",
            };
        }

        const result = await PantheonSessionHelper.requireOnixDesktopResult(
            onixDesktop.readFile(path),
        );

        return result.contents ?? "";
    }

    static async ensureLocalDirectory(path: string): Promise<void> {
        if (PantheonSessionHelper.canUseWindowRequire()) {
            await window.require("fs").promises.mkdir(path, {recursive: true});
            return;
        }

        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        if (!onixDesktop?.makeDirectory) {
            throw {
                stderr: "Ažurirajte OnixDesktop prije kreiranja lokalnih Pantheon mapa.",
            };
        }

        await PantheonSessionHelper.requireOnixDesktopResult(
            onixDesktop.makeDirectory(path),
        );
    }

    static getIniText(selected: PantheonINI): string {
        return (selected as any)?.ini ?? (selected as any)?.INI ?? "";
    }

    static shouldUseMeshCentral(selected: PantheonINI): boolean {
        return !!((selected as any)?.useMeshCentral ?? (selected as any)?.UseMeshCentral);
    }

    static applyLocalSqlServerToIni(ini: string, localPort: number): string {
        const localServerName = `127.0.0.1,${localPort}`;
        const lineEnding = ini.includes("\r\n") ? "\r\n" : "\n";
        const hasTrailingLineEnding =
            ini.endsWith("\r\n") || ini.endsWith("\n") || ini.endsWith("\r");
        const lines = ini.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

        let databaseSectionIndex = -1;
        let nextSectionIndex = lines.length;
        for (let i = 0; i < lines.length; i += 1) {
            const trimmed = lines[i].trim();
            if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) {
                continue;
            }

            if (trimmed.toLowerCase() === "[database]") {
                databaseSectionIndex = i;
                continue;
            }

            if (databaseSectionIndex >= 0) {
                nextSectionIndex = i;
                break;
            }
        }

        if (databaseSectionIndex < 0) {
            if (lines.length > 0 && lines[lines.length - 1].length === 0) {
                lines.pop();
            }

            lines.push("[Database]", `SERVERNAME=${localServerName}`);
            return lines.join(lineEnding) + (hasTrailingLineEnding ? lineEnding : "");
        }

        for (let i = databaseSectionIndex + 1; i < nextSectionIndex; i += 1) {
            const line = lines[i];
            const separatorIndex = line.indexOf("=");
            if (separatorIndex < 0) {
                continue;
            }

            const key = line.slice(0, separatorIndex).trim();
            if (key.toLowerCase() !== "servername") {
                continue;
            }

            let prefixLength = separatorIndex + 1;
            while (prefixLength < line.length && /\s/.test(line[prefixLength])) {
                prefixLength += 1;
            }

            lines[i] = `${line.slice(0, prefixLength)}${localServerName}`;
            return lines.join(lineEnding);
        }

        lines.splice(nextSectionIndex, 0, `SERVERNAME=${localServerName}`);
        return lines.join(lineEnding);
    }

    static async openMeshCentralSqlTunnelIfNeeded(
        selected: PantheonINI,
    ): Promise<MeshCentralSqlTunnel | undefined> {
        if (!PantheonSessionHelper.shouldUseMeshCentral(selected)) {
            return undefined;
        }

        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        if (!onixDesktop?.openPantheonMeshCentralSqlTunnel) {
            throw {
                stderr:
                    "Za ovaj Pantheon INI uključena je opcija Koristi MeshCentral, ali OnixDesktop ne podržava MeshCentral SQL tunel.",
            };
        }

        const result = await PantheonSessionHelper.requireOnixDesktopResult(
            onixDesktop.openPantheonMeshCentralSqlTunnel({
                accessToken: localStorage.getItem("auth_token") ?? undefined,
                pantheonIniId: selected.id,
            }),
        );

        if (!result.localPort || !result.tunnelId) {
            throw {
                stderr: "OnixDesktop nije vratio lokalni MeshCentral SQL tunel.",
            };
        }

        return {
            localPort: result.localPort,
            tunnelId: result.tunnelId,
        };
    }

    static async closeMeshCentralSqlTunnel(
        tunnel?: MeshCentralSqlTunnel,
    ): Promise<void> {
        if (!tunnel) {
            return;
        }

        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        if (!onixDesktop?.closePantheonMeshCentralSqlTunnel) {
            return;
        }

        try {
            await onixDesktop.closePantheonMeshCentralSqlTunnel({
                tunnelId: tunnel.tunnelId,
            });
        } catch (ex) {
            console.warn("Nije moguće zatvoriti MeshCentral SQL tunel", ex);
        }
    }

    static joinLocalPath(basePath: string, fileName: string): string {
        if (PantheonSessionHelper.canUseWindowRequire()) {
            const path = window.require("path");
            return path.join(basePath, fileName);
        }

        const separator = basePath.includes("\\") && !basePath.includes("/") ? "\\" : "/";
        return `${basePath.replace(/[\\/]+$/, "")}${separator}${fileName}`;
    }

    static makeClientSessionId(): string {
        const randomUuid = (window.crypto as any)?.randomUUID?.();

        if (randomUuid) {
            return randomUuid;
        }

        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    static sessionCompanyName(session: PantheonExeSession): string {
        if (session.pantheonIni?.company?.name) {
            return session.pantheonIni.company.name;
        }

        const iniPodjetje = PantheonSessionHelper.getIniText(session.pantheonIni)
            ?.split("\n")
            .find((x: string) => x.startsWith("Podjetje="));

        if (iniPodjetje) {
            return iniPodjetje.replace("Podjetje=", "").trim();
        }

        return "Pantheon";
    }

    static sessionDurationInMilliseconds(session: PantheonExeSession): number {
        const startedAt = new Date(session.startedAt).getTime();
        const endedAt = session.endedAt
            ? new Date(session.endedAt).getTime()
            : new Date().getTime();
        let total = Math.max(0, endedAt - startedAt);

        for (const pastSession of session.pastSessions ?? []) {
            const pastStartedAt = new Date(pastSession.startedAt).getTime();
            const pastEndedAt = pastSession.endedAt
                ? new Date(pastSession.endedAt).getTime()
                : new Date().getTime();
            total += Math.max(0, pastEndedAt - pastStartedAt);
        }

        return total;
    }

    static formatTimeSpanFromMilliseconds(milliseconds: number): string {
        const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const time = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0",
        )}:${String(seconds).padStart(2, "0")}`;

        return days > 0 ? `${days}.${time}` : time;
    }

    static isStaleRunningSession(
        session: PantheonExeSession,
        now = new Date(),
    ): boolean {
        if (session.endedAt || !session.startedAt) {
            return false;
        }

        switch (session.currentStatus) {
            case "pantheonSessionFinished":
            case "pantheonSessionError":
            case "pantheonSessionTerminatedSuccessfully":
            case "pantheonSessionTerminatedWithError":
                return false;
        }

        return (
            now.getTime() - new Date(session.startedAt).getTime() >=
            PantheonSessionHelper.staleRunningSessionMilliseconds
        );
    }

    static isTerminalSessionStatus(status?: PantheonSessionStatus): boolean {
        switch (status) {
            case "pantheonSessionFinished":
            case "pantheonSessionError":
            case "pantheonSessionTerminatedSuccessfully":
            case "pantheonSessionTerminatedWithError":
                return true;
            default:
                return false;
        }
    }

    static isSessionRunningForCount(session: PantheonExeSession): boolean {
        if (
            session.endedAt ||
            PantheonSessionHelper.isTerminalSessionStatus(session.currentStatus)
        ) {
            return false;
        }

        if (session.nativeProcessState) {
            return (
                session.nativeProcessState === "starting" ||
                session.nativeProcessState === "running"
            );
        }

        return true;
    }

    static nativeReconciliationUpdates(
        session: PantheonExeSession,
        state: PantheonNativeProcessStateMessage,
        checkedAt = new Date(),
    ): Partial<PantheonExeSession> {
        const processStartedAt = state.processStartedAt
            ? new Date(state.processStartedAt)
            : session.nativeProcessStartedAt;
        const updates: Partial<PantheonExeSession> = {
            nativeProcessCheckedAt: checkedAt,
            nativeProcessExePath: state.exePath ?? session.nativeProcessExePath,
            nativeProcessId: state.pid ?? session.nativeProcessId,
            nativeProcessIniPath: state.iniPath ?? session.nativeProcessIniPath,
            nativeProcessStartedAt: processStartedAt,
            nativeProcessState: state.state,
        };

        if (state.state === "running") {
            updates.currentStatus = "executingPantheon";
        } else if (
            state.state === "finished" ||
            state.state === "error" ||
            state.state === "missing"
        ) {
            updates.currentStatus =
                state.state === "finished"
                    ? "pantheonSessionTerminatedSuccessfully"
                    : "pantheonSessionTerminatedWithError";
            updates.endedAt = state.endedAt ? new Date(state.endedAt) : checkedAt;
            updates.nativeMeshCentralTunnelId = undefined;
        }

        return updates;
    }

    static async applyNativeProcessState(
        state: PantheonNativeProcessStateMessage,
        checkedAt = new Date(),
    ): Promise<void> {
        if (!db || !state?.clientSessionId) return;

        const session = await db.pantheonExeSessions
            .filter((item) => item.clientSessionId === state.clientSessionId)
            .first();
        if (!session?.id || session.endedAt) return;

        const updates = PantheonSessionHelper.nativeReconciliationUpdates(
            session,
            state,
            checkedAt,
        );
        await db.pantheonExeSessions.update(session.id, updates);

        if (updates.endedAt) {
            if (session.nativeMeshCentralTunnelId) {
                await PantheonSessionHelper.closeMeshCentralSqlTunnel({
                    localPort: 0,
                    tunnelId: session.nativeMeshCentralTunnelId,
                });
            }

            try {
                await PantheonSessionHelper.syncFinishedSession(session.id);
            } catch (ex) {
                console.warn("Nije moguće sinkronizirati završenu Pantheon sesiju", ex);
            }
        }
    }

    static async reconcileNativeProcesses(now = new Date()): Promise<void> {
        if (!db) return;

        const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
        if (!PantheonSessionHelper.hasOnixDesktopPantheonProcessApi()) {
            await PantheonSessionHelper.cleanupStaleRunningSessions(now);
            return;
        }

        const unfinishedSessions = (await db.pantheonExeSessions.toArray()).filter(
            (session) => !session.endedAt,
        );
        const expectedSessions = unfinishedSessions
            .filter(PantheonSessionHelper.isSessionRunningForCount)
            .filter(
                (session) =>
                    !!session.clientSessionId &&
                    !!session.nativeProcessId &&
                    !!session.nativeProcessStartedAt &&
                    !!session.nativeProcessExePath &&
                    !!session.nativeProcessIniPath,
            )
            .map((session) => ({
                clientSessionId: session.clientSessionId!,
                exePath: session.nativeProcessExePath!,
                iniPath: session.nativeProcessIniPath!,
                pid: session.nativeProcessId!,
                processStartedAt: new Date(session.nativeProcessStartedAt!).toISOString(),
            }));

        for (
            let index = 0;
            index < expectedSessions.length;
            index += PantheonSessionHelper.nativeProcessReconciliationBatchSize
        ) {
            const sessions = expectedSessions.slice(
                index,
                index + PantheonSessionHelper.nativeProcessReconciliationBatchSize,
            );
            const result = await PantheonSessionHelper.requireOnixDesktopResult(
                onixDesktop!.reconcilePantheonProcesses!({sessions}),
            );
            for (const state of result.states ?? []) {
                await PantheonSessionHelper.applyNativeProcessState(state, now);
            }
        }

        for (const session of unfinishedSessions) {
            if (!session.id) continue;

            const isTerminalStatus = PantheonSessionHelper.isTerminalSessionStatus(
                session.currentStatus,
            );
            const isTerminalNativeState =
                session.nativeProcessState === "finished" ||
                session.nativeProcessState === "error" ||
                session.nativeProcessState === "missing";
            if (isTerminalStatus || isTerminalNativeState) {
                const isSuccessful =
                    session.currentStatus === "pantheonSessionFinished" ||
                    session.currentStatus === "pantheonSessionTerminatedSuccessfully" ||
                    session.nativeProcessState === "finished";
                const clientSessionId =
                    session.clientSessionId ?? PantheonSessionHelper.makeClientSessionId();
                if (!session.clientSessionId) {
                    await db.pantheonExeSessions.update(session.id, {clientSessionId});
                }
                await PantheonSessionHelper.applyNativeProcessState(
                    {
                        clientSessionId,
                        state: isSuccessful ? "finished" : "error",
                    },
                    now,
                );
                continue;
            }

            const hasNativeIdentity =
                !!session.clientSessionId &&
                !!session.nativeProcessId &&
                !!session.nativeProcessStartedAt &&
                !!session.nativeProcessExePath &&
                !!session.nativeProcessIniPath;
            const isStartingWithinGrace =
                session.nativeProcessState === "starting" &&
                now.getTime() - new Date(session.startedAt).getTime() <
                PantheonSessionHelper.nativeProcessLaunchGraceMilliseconds;

            if (hasNativeIdentity || isStartingWithinGrace) {
                continue;
            }

            const clientSessionId =
                session.clientSessionId ?? PantheonSessionHelper.makeClientSessionId();
            if (!session.clientSessionId) {
                await db.pantheonExeSessions.update(session.id, {clientSessionId});
            }

            await PantheonSessionHelper.applyNativeProcessState(
                {
                    clientSessionId,
                    state: "missing",
                },
                now,
            );
        }
    }

    static staleRunningSessionEndedAt(session: PantheonExeSession): Date {
        return new Date(
            new Date(session.startedAt).getTime() +
            PantheonSessionHelper.staleRunningSessionMilliseconds,
        );
    }

    static async cleanupStaleRunningSessions(now = new Date()): Promise<void> {
        if (!db) return;

        const sessions = await db.pantheonExeSessions.toArray();
        for (const session of sessions) {
            if (!session.id || !PantheonSessionHelper.isStaleRunningSession(session, now)) {
                continue;
            }

            await db.pantheonExeSessions.update(session.id, {
                currentStatus: "pantheonSessionTerminatedWithError",
                endedAt: PantheonSessionHelper.staleRunningSessionEndedAt(session),
            });

            try {
                await PantheonSessionHelper.syncFinishedSession(session.id);
            } catch (ex) {
                console.warn("Nije moguće sinkronizirati zastarjelu Pantheon sesiju", ex);
            }
        }
    }

    static buildFinishedSessionRequest(session: PantheonExeSession) {
        const companyName = PantheonSessionHelper.sessionCompanyName(session);
        const duration = PantheonSessionHelper.formatTimeSpanFromMilliseconds(
            PantheonSessionHelper.sessionDurationInMilliseconds(session),
        );

        return {
            clientSessionId: session.clientSessionId,
            pantheonIniId: session.pantheonIniId,
            pantheonCompanyName: companyName,
            status: session.currentStatus,
            startedAt: session.startedAt,
            endedAt: session.endedAt,
            duration,
            note: `Pantheon sesija: ${companyName}`,
            supportTicketId: session.ticketId,
        };
    }

    static async syncFinishedSession(id: IndexableType): Promise<number | undefined> {
        if (!db) return;
        const session = await db.pantheonExeSessions.get(id);

        if (!session?.endedAt) {
            return session?.serverSessionId;
        }

        let clientSessionId = session.clientSessionId;

        if (!clientSessionId) {
            clientSessionId = PantheonSessionHelper.makeClientSessionId();
            await db.pantheonExeSessions.update(id, {clientSessionId});
        }

        const {data: serverSessionId} = await axios.post<number>(
            "PantheonSessions/Finished",
            PantheonSessionHelper.buildFinishedSessionRequest({...session, clientSessionId}),
        );

        await db.pantheonExeSessions.update(id, {
            clientSessionId,
            serverSessionId,
            serverSyncedAt: new Date(),
        });

        return serverSessionId;
    }

    static async dismissServerSession(session: PantheonExeSession): Promise<void> {
        if (!session.serverSessionId) {
            return;
        }

        try {
            await axios.post(`PantheonSessions/${session.serverSessionId}/Dismiss`);
        } catch (ex) {
            console.warn("Nije moguće sakriti Pantheon sesiju na serveru", ex);
        }
    }

    static async electronDownload(url: string, exePath: string): Promise<void> {
        const fs = window.require("fs");
        const http = url.startsWith("https") ? window.require("https") : window.require("http");
        const file = fs.createWriteStream(exePath);
        const request = http.get(url);
        return new Promise((resolve, reject) => {
            request
                .on("response", (response: any) => {
                    response.pipe(file);
                    file.on("finish", () => {
                        file.close();
                        resolve();
                    });
                })
                .on("error", (err: any) => {
                    fs.unlink(exePath, () => {
                    });
                    reject(err);
                });
        });
    }

    static async mergeAll(): Promise<void> {
        if (!db) return;
        const sessions = await db.pantheonExeSessions.toArray();

        // const firstSession = sessions[0];

        // if (firstSession) {
        //     for (let i = 1; i < sessions.length; i++) {
        //         const session = sessions[i];
        //         // if (session) {
        //         //     await db.pantheonExeSessions.update(firstSession.id!, {
        //         //         pastSessions: [...(firstSession.pastSessions ?? []), ...(session.pastSessions ?? []), {
        //         //             startedAt: session.startedAt,
        //         //             endedAt: session.endedAt
        //         //         }]
        //         //     });
        //         //     await db.pantheonExeSessions.delete(session.id!);
        //         // }
        //     }
        // }

        // group by company
        const sessionsByCompany: { [companyId: number]: PantheonExeSession[] } = {};
        for (const session of sessions) {
            if (!sessionsByCompany[session.companyId!]) {
                sessionsByCompany[session.companyId!] = [];
            }
            sessionsByCompany[session.companyId!].push(session);
        }

        for (const companyId of Object.keys(sessionsByCompany)) {
            const sessions = sessionsByCompany[+companyId!];
            const firstSession = sessions[0];
            if (firstSession) {
                let pastSessions = [...(firstSession.pastSessions ?? [])];
                for (let i = 1; i < sessions.length; i++) {
                    const session = sessions[i];
                    if (session) {
                        pastSessions = [
                            ...pastSessions,
                            ...(session.pastSessions ?? []),
                            {
                                startedAt: session.startedAt,
                                endedAt: session.endedAt ?? new Date(),
                            },
                        ];
                        await db.pantheonExeSessions.update(firstSession.id!, {
                            pastSessions,
                        });
                        await PantheonSessionHelper.dismissServerSession(session);
                        await db.pantheonExeSessions.delete(session.id!);
                    }
                }
                if (firstSession.endedAt) {
                    try {
                        await PantheonSessionHelper.syncFinishedSession(firstSession.id!);
                    } catch (ex) {
                        console.warn("Nije moguće sinkronizirati Pantheon sesiju", ex);
                    }
                }
            }
        }
    }

    static async extract7zip(
        z7Path: string,
        outPath: string,
    ): Promise<{ files: { status: string; file: string }[] }> {
        return new Promise((resolve, reject) => {
            const z7Lib = window.require("node-7z");
            const files: { status: string; file: string }[] = [];
            console.log("extracting", z7Path, "to", outPath);
            const process = z7Lib.extractFull(z7Path, outPath);
            process.on("data", (file: { status: string; file: string }) =>
                files.push(file),
            );
            process.on("end", () => resolve({files}));
            process.on("error", reject);
        });
    }

    static async setSessionStatus(
        id: IndexableType,
        status?: PantheonSessionStatus,
    ): Promise<void> {
        if (!db) return;
        const newSessionData: Partial<PantheonExeSession> = {
            currentStatus: status,
        };
        switch (status) {
            case "pantheonSessionTerminatedSuccessfully":
            case "pantheonSessionTerminatedWithError":
                newSessionData.endedAt = new Date();
                break;
        }
        await db.pantheonExeSessions.update(id, newSessionData);
        if (newSessionData.endedAt) {
            try {
                await PantheonSessionHelper.syncFinishedSession(id);
            } catch (ex) {
                console.warn("Nije moguće sinkronizirati završenu Pantheon sesiju", ex);
            }
        }
    }

    static async restartPantheonSession(id: IndexableType): Promise<void> {
        if (!db) return;
        const session = await db.pantheonExeSessions.get(id);
        if (session) {
            await this.startPantheonSession(
                session.pantheonIni,
                session.platformOverride,
                session.UserInfo,
                session.ticketId,
                session,
            );
        }
    }

    static async startPantheonSessionFromDeepLink(
        url: URL,
        UserInfo: UserQuery | undefined,
    ) {
        const query = url.searchParams;
        const parsedQueryData = JSON.parse(
            decodeURIComponent(query.get("data")!),
        ) as {
            selected?: number;
            selectedId: number;
            platformOverride?: "win32" | "darwin";
            ticketId?: number;
        };
        const selectedId = parsedQueryData.selectedId ?? parsedQueryData.selected;
        const platformOverride = parsedQueryData.platformOverride;
        const ticketId = parsedQueryData.ticketId;

        if (!selectedId) {
            throw new Error("Pantheon INI ID nije pronađen u Onix deep linku.");
        }

        const selected = await axios.get<PantheonINI>(
            `/PantheonInis/${selectedId}`,
        );

        await this.startPantheonSession(
            selected.data,
            platformOverride,
            UserInfo,
            ticketId,
        );
    }

    static async deleteSession(id: IndexableType): Promise<void> {
        if (!db) return;
        const session = await db.pantheonExeSessions.get(id);
        if (session) {
            await PantheonSessionHelper.dismissServerSession(session);
        }
        await db.pantheonExeSessions.delete(id);
    }
    
    static async deleteAllSessions(): Promise<void> {
        if (!db) return;
        const sessions = await db.pantheonExeSessions.toArray();
        await Promise.all(
            sessions.map((session) => PantheonSessionHelper.dismissServerSession(session)),
        );
        await db.pantheonExeSessions.clear();
    }

    static makeIniPath(selected: PantheonINI, userIniPath: string): string {
        const iniName = `${selected.id}-${selected.companyId}.ini`;
        return PantheonSessionHelper.joinLocalPath(userIniPath, iniName);
    }

    static makeExePath(selected: PantheonINI, userExePath: string): string {
        return `${userExePath}/Pantheon${selected.pantheonVersion.group}_${selected.pantheonVersion.version}.exe`;
    }

    static async startPantheonSession(
        selected: PantheonINI,
        platformOverride?: "win32" | "darwin",
        UserInfo?: UserQuery,
        ticketId?: number,
        asContinuationOf?: PantheonExeSession,
    ): Promise<void> {
        if (!db) return;
        try {
            const canUseWindowRequire = PantheonSessionHelper.canUseWindowRequire();
            const onixDesktop = PantheonSessionHelper.getOnixDesktopApi();
            const canTrackNativeProcess =
                !canUseWindowRequire &&
                PantheonSessionHelper.hasOnixDesktopPantheonProcessApi();
            if (!canUseWindowRequire && !PantheonSessionHelper.hasOnixDesktopPantheonApi()) {
                window.alert("Ažurirajte OnixDesktop prije pokretanja Pantheon sesije.");
                return;
            }

            const realPlatform = canUseWindowRequire
                ? window.require("os").platform()
                : (
                    await PantheonSessionHelper.requireOnixDesktopResult(
                        onixDesktop!.getPlatform!(),
                    )
                ).platform;
            const platform = platformOverride ?? realPlatform;

            console.log("platform", platform);

            let userExePath: string | undefined = "";
            let userIniPath: string | undefined = "";

            if (platform === "darwin") {
                userExePath = UserInfo?.user?.onixDesktopPantheonExePathMac;
                userIniPath = UserInfo?.user?.onixDesktopPantheonIniPathMac;
            } else {
                userExePath = UserInfo?.user?.onixDesktopPantheonExePath;
                userIniPath = UserInfo?.user?.onixDesktopPantheonIniPath;
            }

            if (!selected.pantheonVersion) {
                window.alert("Postavite verziju Pantheona za INI!");
            } else if (!userExePath || !userIniPath) {
                window.alert("Postavite EXE i INI putanju u korisničkim postavkama!");
            } else {
                const clientSessionId = PantheonSessionHelper.makeClientSessionId();
                let sessionId: IndexableType;
                if (asContinuationOf) {
                    sessionId = asContinuationOf.id!;
                    const session = (await db.pantheonExeSessions.get(sessionId))!;
                    await PantheonSessionHelper.dismissServerSession(session);
                    await db.pantheonExeSessions.update(sessionId, {
                        ...session,
                        pastSessions: [
                            ...(session.pastSessions ?? []),
                            {
                                startedAt: session.startedAt,
                                endedAt: session.endedAt ?? new Date(),
                            },
                        ],
                        clientSessionId,
                        startedAt: new Date(),
                        endedAt: undefined,
                        currentStatus: undefined,
                        nativeProcessCheckedAt: undefined,
                        nativeProcessExePath: undefined,
                        nativeProcessId: undefined,
                        nativeProcessIniPath: undefined,
                        nativeProcessStartedAt: undefined,
                        nativeProcessState: canTrackNativeProcess ? "starting" : undefined,
                        nativeMeshCentralTunnelId: undefined,
                        serverSessionId: undefined,
                        serverSyncedAt: undefined,
                    });
                } else {
                    sessionId = await db.pantheonExeSessions.add({
                        clientSessionId,
                        startedAt: new Date(),
                        companyId: selected.companyId,
                        ticketId,
                        pantheonIni: selected,
                        platformOverride,
                        UserInfo,
                        pantheonIniId: selected.id,
                        nativeProcessState: canTrackNativeProcess ? "starting" : undefined,
                    });
                }
                let meshCentralTunnel: MeshCentralSqlTunnel | undefined;
                let nativeProcessTrackingStarted = false;
                try {
                    await PantheonSessionHelper.setSessionStatus(
                        sessionId,
                        "pantheonSessionStarted",
                    );
                    await PantheonSessionHelper.ensureLocalDirectory(userExePath);
                    await PantheonSessionHelper.ensureLocalDirectory(userIniPath);

                    const fs = canUseWindowRequire ? window.require("fs") : undefined;
                    const util = canUseWindowRequire ? window.require("util") : undefined;
                    const existsAsync = canUseWindowRequire
                        ? util.promisify(fs.exists)
                        : async (path: string) =>
                            (
                                await PantheonSessionHelper.requireOnixDesktopResult(
                                    onixDesktop!.fileExists!(path),
                                )
                            ).exists;
                    const writeFileAsync = canUseWindowRequire
                        ? util.promisify(fs.writeFile)
                        : async (path: string, contents: string) => {
                            await PantheonSessionHelper.requireOnixDesktopResult(
                                onixDesktop!.writeFile!(path, contents),
                            );
                        };
                    const downloadFileAsync = canUseWindowRequire
                        ? PantheonSessionHelper.electronDownload
                        : async (url: string, destinationPath: string) => {
                            await PantheonSessionHelper.requireOnixDesktopResult(
                                onixDesktop!.downloadFile!({destinationPath, url}),
                            );
                        };
                    const extract7zipAsync = canUseWindowRequire
                        ? PantheonSessionHelper.extract7zip
                        : async (archivePath: string, destinationPath: string) => {
                            await PantheonSessionHelper.requireOnixDesktopResult(
                                onixDesktop!.extract7zip!({
                                    archivePath,
                                    destinationPath,
                                }),
                            );
                        };
                    const renameFileAsync = canUseWindowRequire
                        ? fs.promises.rename
                        : async (fromPath: string, toPath: string) => {
                            await PantheonSessionHelper.requireOnixDesktopResult(
                                onixDesktop!.renameFile!({fromPath, toPath}),
                            );
                        };

                    const iniPath = PantheonSessionHelper.makeIniPath(
                        selected,
                        userIniPath,
                    );
                    const exePath = PantheonSessionHelper.makeExePath(
                        selected,
                        userExePath,
                    );

                    console.log("iniPath", iniPath);
                    console.log("exePath", exePath);

                    if (!(await existsAsync(exePath))) {
                        console.warn("Ne postoji Pantheon exe ", exePath, "preuzimam");
                        await PantheonSessionHelper.setSessionStatus(
                            sessionId,
                            "downloadingPantheonExe",
                        );

                        const urlPrefix = selected.pantheonVersion.downloadPath.startsWith(
                            "http",
                        )
                            ? ""
                            : selected.pantheonVersion.downloadPath.startsWith("ftps") ? "https://" : "http://";

                        const z7Filename = `Pantheon${selected.pantheonVersion.group}_${selected.pantheonVersion.version}.7z`;
                        const z7Path = canUseWindowRequire
                            ? z7Filename
                            : PantheonSessionHelper.joinLocalPath(userExePath, z7Filename);

                        if (!(await existsAsync(z7Path))) {
                            await downloadFileAsync(
                                `${urlPrefix}/${selected.pantheonVersion.downloadPath}`,
                                z7Path,
                            );
                        }

                        await PantheonSessionHelper.setSessionStatus(
                            sessionId,
                            "extractingPantheonExe",
                        );

                        await extract7zipAsync(z7Path, userExePath);

                        await PantheonSessionHelper.setSessionStatus(
                            sessionId,
                            "movingExtractedPantheonExe",
                        );

                        let pantheonExeName = `Pantheon${selected.pantheonVersion.group}.exe`;

                        if (
                            selected.pantheonVersion.group?.trim().toLowerCase() == "upgrade"
                        ) {
                            pantheonExeName = `PA${selected.pantheonVersion.group}.exe`;
                        }

                        const extractedExePath = PantheonSessionHelper.joinLocalPath(
                            userExePath,
                            pantheonExeName,
                        );

                        if (!(await existsAsync(extractedExePath))) {
                            window.alert("Ne postoji Pantheon.exe u arhivi!");
                            return;
                        }

                        await renameFileAsync(extractedExePath, exePath);
                    }

                    // fs.chmodSync(exePath, 0o400);

                    await PantheonSessionHelper.setSessionStatus(sessionId, "writingIni");
                    meshCentralTunnel =
                        await PantheonSessionHelper.openMeshCentralSqlTunnelIfNeeded(
                            selected,
                        );
                    if (meshCentralTunnel) {
                        await db.pantheonExeSessions.update(sessionId, {
                            nativeMeshCentralTunnelId: meshCentralTunnel.tunnelId,
                        });
                    }
                    const ini = meshCentralTunnel
                        ? PantheonSessionHelper.applyLocalSqlServerToIni(
                            PantheonSessionHelper.getIniText(selected),
                            meshCentralTunnel.localPort,
                        )
                        : PantheonSessionHelper.getIniText(selected);
                    await writeFileAsync(iniPath, ini);
                    await PantheonSessionHelper.setSessionStatus(
                        sessionId,
                        "executingPantheon",
                    );
                    const execAsync = canUseWindowRequire
                        ? util.promisify(window.require("child_process").exec)
                        : undefined;

                    let pantheonLicenseArgument = "";

                    if (selected.license !== null && selected.license !== undefined) {
                        switch (selected.license) {
                            case PantheonLicense.RT:
                                pantheonLicenseArgument = "/RT";
                                break;
                            case PantheonLicense.LX:
                                pantheonLicenseArgument = "/LX";
                                break;
                            case PantheonLicense.LT:
                                pantheonLicenseArgument = "/LT";
                                break;
                            case PantheonLicense.LT3:
                                pantheonLicenseArgument = "/LT3";
                                break;
                            case PantheonLicense.RE:
                                pantheonLicenseArgument = "/RE";
                                break;
                            case PantheonLicense.SE:
                                pantheonLicenseArgument = "/SE";
                                break;
                            case PantheonLicense.ME:
                                pantheonLicenseArgument = "/ME";
                                break;
                            case PantheonLicense.MF:
                                pantheonLicenseArgument = "/MF";
                                break;
                        }
                    }

                    let userName = "";
                    let password = "";
                    let authArgument = "";

                    if (!!selected.userName) {
                        userName = selected.userName;
                    }

                    if (!!selected.password) {
                        password = selected.password;
                    }

                    if (!!userName && !!password) {
                        authArgument = `/L=${userName}:${password}`;
                    }

                    if (!canUseWindowRequire) {
                        const processState = await PantheonSessionHelper.requireOnixDesktopResult(
                            onixDesktop!.executePantheon!({
                                clientSessionId,
                                authArgument,
                                exePath,
                                iniPath,
                                pantheonLicenseArgument,
                                platform,
                            }),
                        );

                        if (processState.pid && processState.processStartedAt) {
                            nativeProcessTrackingStarted = true;
                            const session = await db.pantheonExeSessions.get(sessionId);
                            if (session && !session.endedAt) {
                                await db.pantheonExeSessions.update(
                                    sessionId,
                                    PantheonSessionHelper.nativeReconciliationUpdates(
                                        session,
                                        processState,
                                    ),
                                );
                            }
                        }
                    } else if (platform === "darwin") {
                        // mac
                        const pathToCrossover =
                            "/Applications/CrossOver.app/Contents/SharedSupport/CrossOver/CrossOver-Hosted Application/wine";
                        const command = `export CX_BOTTLE=Windows; "${pathToCrossover}" "${exePath}" "/INI=${iniPath}" ${pantheonLicenseArgument} ${authArgument}`;
                        await execAsync(command);
                    } else if (realPlatform === "win32") {
                        // windows
                        const command = `"${exePath}" "/INI=${iniPath}" ${pantheonLicenseArgument} ${authArgument}`;
                        await execAsync(command);
                    } else if (realPlatform === "darwin" && platform === "win32") {
                        // parallels
                        const command = `start "" "${exePath}" "/INI=${iniPath}" ${pantheonLicenseArgument} ${authArgument}`;
                        await execAsync(command);
                    }

                    if (!nativeProcessTrackingStarted) {
                        await PantheonSessionHelper.setSessionStatus(
                            sessionId,
                            "pantheonSessionTerminatedSuccessfully",
                        );
                    }
                } catch (ex) {
                    console.error(ex);
                    if (!(ex as any)?.["stderr"]) {
                        await PantheonSessionHelper.setSessionStatus(
                            sessionId,
                            "pantheonSessionTerminatedSuccessfully",
                        );
                    } else {
                        alert(`Greška u sesiji: ${JSON.stringify(ex)}`);
                        await PantheonSessionHelper.setSessionStatus(
                            sessionId,
                            "pantheonSessionTerminatedWithError",
                        );
                    }
                } finally {
                    if (!nativeProcessTrackingStarted) {
                        await PantheonSessionHelper.closeMeshCentralSqlTunnel(
                            meshCentralTunnel,
                        );
                    }
                }
            }
        } catch (ex) {
            console.error(ex);
            alert(`Opća greška: ${JSON.stringify(ex)}`);
        }
    }
}
